/**
 * SELL-04 listing rules shared by the listing editor and the intake wizard.
 * Prices are entered in rupees and stored in paise. Pure: no I/O.
 */
import { rupeesToPaise } from './money';

const isInt = (v, min, max) => /^\d+$/.test(String(v)) && Number(v) >= min && Number(v) <= max;

/**
 * f: { title, price, mrp, warranty_months, status }
 * ctx: { canPublish, blockers: [labels of failed safety checks] }
 * Returns { field: message }.
 */
export function validateListing(f, { canPublish = false, blockers = [] } = {}) {
  const e = {};
  const price = rupeesToPaise(f.price);
  const mrp = f.mrp === '' || f.mrp == null ? null : rupeesToPaise(f.mrp);
  if (!String(f.title || '').trim()) e.title = 'Title is required';
  else if (String(f.title).trim().length > 200) e.title = 'Keep the title under 200 characters';
  if (price == null || price <= 0) e.price = 'Enter a selling price in rupees';
  if (f.mrp !== '' && f.mrp != null && mrp == null) e.mrp = 'Enter an amount in rupees';
  else if (mrp != null && price != null && mrp < price) e.mrp = 'MRP cannot be below the selling price';
  if (!isInt(f.warranty_months, 0, 60)) e.warranty_months = 'Warranty is 0–60 months';
  if (f.status === 'published') {
    if (!canPublish) e.status = 'You do not have permission to publish';
    else if (blockers.length) e.status = `Cannot publish: ${blockers.join(', ')} not passed`;
  }
  return e;
}

export function listingPayload(f) {
  return {
    title: String(f.title).trim(),
    price_paise: rupeesToPaise(f.price),
    mrp_paise: f.mrp === '' || f.mrp == null ? null : rupeesToPaise(f.mrp),
    warranty_months: Number(f.warranty_months),
    status: f.status,
  };
}

/**
 * The one-line readiness flag a list row shows (MERCH-58), from a readiness result
 * ({ ready, missing: [{ key }] }). The first missing rule, in this order, names it.
 */
const FLAGS = [
  ['unit', 'No stock unit', 'error'],
  ['not_sourced', 'Dropshipped', 'secondary'],
  ['stock_state', 'Not in stock', 'secondary'],
  ['lot_stock', 'No stock on hand', 'warning'],
  ['safety', 'Failed safety check', 'error'],
  ['inspection', 'Inspection needed', 'warning'],
  ['photos', 'Photos needed', 'warning'],
];

/** → { label, variant } (twreact-ui Badge variants), or null while unknown. */
export function readinessFlag(r) {
  if (!r) return null;
  if (r.ready) return { label: 'Ready', variant: 'success' };
  const keys = new Set((r.missing || []).map((m) => m.key));
  const hit = FLAGS.find(([key]) => keys.has(key));
  return hit ? { label: hit[1], variant: hit[2] } : { label: 'Not ready', variant: 'warning' };
}

/** Row-menu entries for a listing (MERCH-58): what this person may do with it now. */
export function listingMenu(l, { canUpdate = false, canPublish = false, ready = false } = {}) {
  const items = [{ key: 'edit', label: canUpdate ? 'Edit' : 'Open' }];
  if (l.status === 'published') items.push({ key: 'view', label: 'View on storefront' });
  if (canPublish && ['draft', 'unpublished'].includes(l.status)) items.push({ key: 'publish', label: ready ? 'Publish' : 'Publish (not ready)', disabled: !ready });
  if (canPublish && l.status === 'published') items.push({ key: 'unpublish', label: 'Unpublish', tone: 'destructive' });
  return items;
}
